import React, { useState, useEffect } from 'react';
import useShortCode from '../../../../../hooks/useShortCode';
import useFetch, { useCacheActions } from '../../../../../hooks/useFetch';
import SettingsService from '../../../../../services/settings-service';
import usePush from '../../../../../hooks/usePush';
import { ButtonGroup, MultiSelect, Text, Button, Stack } from '@chaser/components';
import OrganisationService from '../../../../../services/organisation-service';
import { shallowEqual } from '../../../../../util';

const options = [
  { id: 'company', label: 'Company' },
  { id: 'contact', label: 'Contact' },
  { id: 'invoice', label: 'Invoice' },
  { id: 'delivery', label: 'Delivery' },
  { id: 'followup', label: 'Follow-up Address' },
  { id: 'other', label: 'Other' },
];

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

const CustomerRecipientsVisibleLabels = () => {
  const shortCode = useShortCode();
  const { invalidateEndpoint } = useCacheActions();
  const { data, applyOptimisticUpdate, refetch } = useFetch(
    SettingsService.getIntegrationFeatures,
    shortCode,
  );

  const [selectedValues, setSelectedValues] = useState([]);

  useEffect(() => {
    if (data && data.customer_recipients_visible_labels) {
      setSelectedValues(
        (data.customer_recipients_visible_labels || []).map(value => ({
          id: value,
          label: options.find(option => option.id === value)?.label || value,
        })),
      );
    }
  }, [data]);

  const { execute: executeLabelsUpdate, loading } = usePush(
    async () => {
      const currentSelection = [...selectedValues];
      const valuesNormalized = currentSelection.map(value => value.id);

      const promise = SettingsService.setCustomerRecipientsVisibleLabels(
        shortCode,
        valuesNormalized,
      );

      applyOptimisticUpdate(state => {
        state.customer_recipients_visible_labels = valuesNormalized;
      }, promise);

      promise.then(async () => {
        await OrganisationService.resetLastRefreshDate(shortCode);
        await Promise.all([OrganisationService.sync(shortCode), wait(1500)]);
        await refetch();
        invalidateEndpoint(OrganisationService.get);
      });
    },
    {
      errorToast: true,
      successToast: 'Customer recipients visible labels updated',
    },
  );

  const hasChanges = !shallowEqual(
    selectedValues.map(v => v.id),
    data.customer_recipients_visible_labels || [],
  );

  return (
    <Stack>
      <Text>Choose which contacts should be visible in Chaser.</Text>
      <Stack gap="small">
        <MultiSelect
          width="300px"
          grouped={false}
          closeMenuOnSelect={false}
          selections={options}
          onChange={setSelectedValues}
          value={selectedValues}
          disabled={loading}
          placeholder="Select contacts"
        />
        <ButtonGroup>
          <Button
            loading={loading}
            appearance="secondary"
            type="button"
            onClick={executeLabelsUpdate}
            disabled={!hasChanges || loading}
          >
            Apply
          </Button>
        </ButtonGroup>
      </Stack>
    </Stack>
  );
};

export default CustomerRecipientsVisibleLabels;
